import translation from 'locales/translation';
import { useCallback, useEffect, useReducer } from 'react';

import api from 'api/Assessor/evento';
import ErrorHandler from 'helpers/utils/ErrorHandler';

import { Col, Row, Select } from 'plataforma-fundacao-componentes';
import { filtrosInitialState, filtrosReducer } from './reducer';

interface FilterParams {
  agencia?: string[];
  nucleo?: string[];
  quorumAtingidoOrRepresentados?: string;
  flgTodos?: string;
}

interface Props {
  idEvento: number;
  onChangeFilter: (params: FilterParams) => void;
  tipoQuorum: string;
  disableFlgTodos: boolean;
}

export default function Filtros({
  idEvento,
  onChangeFilter,
  tipoQuorum,
  disableFlgTodos,
}: Props) {
  const [{ agencia, nucleo, flgTodos, listaAgencias, listaNucleos }, action] =
    useReducer(filtrosReducer, filtrosInitialState);

  const getAgencias = useCallback(async () => {
    const { fail, data } = await api.listarAgencias(idEvento);
    if (fail) {
      ErrorHandler(fail);
    }
    if (data) {
      action({ type: 'setListaAgencias', value: data });
    }
  }, [idEvento]);

  const getNucleos = useCallback(() => {
    if (listaAgencias) {
      const currentAgencia = listaAgencias.find(
        (entry) => agencia[0] === entry.agencia
      );
      action({ type: 'setListaNucleos', value: currentAgencia?.nucleos || [] });
    }
  }, [listaAgencias, agencia]);

  const handleAgenciaChange = useCallback(
    (event: any) => {
      const value = event;
      const needClearNucleos =
        value && nucleo && value.length !== 1 && nucleo.length > 0;
      if (needClearNucleos) {
        action({ type: 'setAgencia', value });
        action({ type: 'setNucleo', value: [] });
        onChangeFilter({ agencia: value, nucleo: [] });
      } else {
        action({ type: 'setAgencia', value });
        onChangeFilter({ agencia: value });
      }
    },
    [onChangeFilter, nucleo]
  );

  const handleNucleoChange = useCallback(
    (event: any) => {
      const value = event;
      action({ type: 'setNucleo', value });
      onChangeFilter({ nucleo: value });
    },
    [onChangeFilter]
  );

  const handleFlgTodosChange = useCallback(
    (event: any) => {
      const value = event;
      action({ type: 'setFlgTodos', value });
      onChangeFilter({ flgTodos: value });
    },
    [onChangeFilter]
  );
  useEffect(() => {
    getAgencias();
  }, [getAgencias]);

  useEffect(() => {
    getNucleos();
  }, [getNucleos]);

  return (
    <Row>
      <Col cols={[12, 12, 4, 4, 4]}>
        <Select
          data-testid={'select-agency'}
          label={translation.RelatorioPresentes.labelFiltroAgencias}
          options={listaAgencias!?.map((item: any) => {
            return {
              label: item.agencia,
              value: item.agencia,
            };
          })}
          onChange={handleAgenciaChange}
          placeholder={'Selecione a agência'}
          value={agencia}
          disabled={!listaAgencias.length}
          multiple
        />
      </Col>
      <Col cols={[12, 12, 4, 4, 4]}>
        <Select
          data-testid={'select-nucleou'}
          label={translation.RelatorioPresentes.labelFiltroNucleos}
          options={listaNucleos!?.map((item: any) => {
            return {
              label: item.nucleo,
              value: item.nucleo,
            };
          })}
          onChange={handleNucleoChange}
          placeholder={'Selecione o núcleo'}
          value={nucleo}
          disabled={!listaNucleos.length}
          multiple
        />
      </Col>
      {!disableFlgTodos && (
        <Col cols={[12, 12, 4, 4, 4]}>
          <Select
            data-testid={'select-quorum'}
            label={tipoQuorum}
            options={[
              {
                label: 'Todos',
                value: 'Todos',
              },
              {
                label: 'Sim',
                value: 'Sim',
              },
              {
                label: 'Não',
                value: 'Não',
              },
            ]}
            onChange={handleFlgTodosChange}
            value={flgTodos}
            clearButton={false}
          />
        </Col>
      )}
    </Row>
  );
}
